/**
 * @module components/ambient
 *
 * The fixed background layer: a perspective grid, two coloured glows and a
 * grain overlay.
 *
 * These are the deepest, slowest-moving elements on the page, and that is what
 * gives the experience its sense of depth. They only ever animate `y` and
 * `scale`, so they never trigger layout, and they sit behind everything with
 * `pointer-events: none`.
 *
 * The glows also breathe via a CSS keyframe animation (see `animations.css`),
 * so the page still has life if the GSAP bundle fails to load.
 */

import { gsap, EASE, MEDIA, createMotionContext } from "../core/gsap-setup.js";
import { qs } from "../core/dom.js";

/**
 * @returns {() => void} Teardown.
 */
export function initAmbient() {
  const grid = qs(".ambient__grid");
  const glowA = qs(".ambient__glow--a");
  const glowB = qs(".ambient__glow--b");
  if (!grid && !glowA && !glowB) return () => {};

  const mm = createMotionContext();

  // Reduced motion: the CSS keyframes are disabled and no parallax is created.
  mm.add(MEDIA.reduce, () => {});

  mm.add(MEDIA.any, () => {
    const tweens = [];

    if (grid) {
      tweens.push(
        gsap.to(grid, {
          yPercent: -9,
          ease: EASE.linear,
          scrollTrigger: { start: 0, end: "max", scrub: 1.2 },
        })
      );
    }

    if (glowA) {
      tweens.push(
        gsap.to(glowA, {
          yPercent: 36,
          scale: 1.28,
          ease: EASE.linear,
          scrollTrigger: { start: 0, end: "max", scrub: 2 },
        })
      );
    }

    if (glowB) {
      tweens.push(
        gsap.to(glowB, {
          yPercent: -28,
          scale: 1.18,
          ease: EASE.linear,
          scrollTrigger: { start: 0, end: "max", scrub: 2.4 },
        })
      );
    }

    return () => {
      tweens.forEach((tween) => {
        tween.scrollTrigger?.kill();
        tween.kill();
      });
    };
  });

  return () => mm.revert();
}
